import { PlusOutlined } from '@ant-design/icons';
import { Button, Col, DatePicker, Form, Row, Table } from 'antd';
import { useForm } from 'antd/es/form/Form';
import { TimeRangePickerProps } from 'antd/lib';
import dayjs from 'dayjs';
import { useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { IPermission } from '../../../common/types/commonTypes';
import { debounce } from '../../../common/utils/common.utils';
import usePagination from '../../../common/utils/usePagination';
import useUrlParams from '../../../common/utils/useUrlParams';
import BreadCrumb from '../../../components/common/breadCrumb/BreadCrumb';
import { FormInput } from '../../../components/common/FormItem/FormItems';
import { loadingIndicator } from '../../../components/common/spinner/LoadingIndicator';
import { formatDateRangeArray, parseDateRangeObject } from '../../../helpers/healper';
import { useGetAllQuotationQuery } from '../Api/Endpoints/QuotationEndpoints';
import { QuotationUtils } from '../Utils/QuotationUtils';

type Props = { permission?: IPermission };

const QuotationList = ({ permission }: Props) => {
  const [form] = useForm();
  const { setUrlParam, setUrlParamsFromObject, getAllUrlParams } = useUrlParams();
  const { page, size, changePagination, pagination } = usePagination();
  const params_value = getAllUrlParams() as {
    search: string | null;
    from_date: string | null;
    to_date: string | null;
  };

  const {
    data,
    isFetching,
    refetch: getAllRefetch,
    isLoading: dataIsLoading,
  } = useGetAllQuotationQuery({
    ...getAllUrlParams(),
    page,
    size,
  });
  const handelSearch = useCallback(
    debounce((e: string) => {
      setUrlParam('search', e);
    }, 600),
    []
  );
  // Add safety check for data mapping
  const dataWithKey =
    data?.data?.map((item, index) => ({
      key: String(index),
      ...item,
    })) || [];

  useEffect(() => {
    form.setFieldsValue({
      search: params_value.search || '',
      date_range: parseDateRangeObject({
        from_date: params_value.from_date,
        to_date: params_value.to_date,
      }),
    });
  }, [params_value]);
  const rangePresets: TimeRangePickerProps['presets'] = [
    { label: 'Today', value: [dayjs(), dayjs()] },
    { label: 'Yesterday', value: [dayjs().add(-1, 'd'), dayjs()] },
    { label: 'Last 7 Days', value: [dayjs().add(-7, 'd'), dayjs()] },
    { label: 'Last 14 Days', value: [dayjs().add(-14, 'd'), dayjs()] },
    { label: 'Last 30 Days', value: [dayjs().add(-30, 'd'), dayjs()] },
    { label: 'Last 90 Days', value: [dayjs().add(-90, 'd'), dayjs()] },
  ];
  return (
    <div>
      <BreadCrumb arrOfOption={['Quotation']} reloaderSize='small' />
      <Row gutter={[10, 10]} justify={'space-between'}>
        <Link to='/quotation/add'>
          <Button type='primary'>
            <PlusOutlined />
            Add Quotation
          </Button>
        </Link>

        <Col span={12}>
          <Form form={form}>
            <Row justify={'end'} gutter={[10, 10]}>
              <Col xs={24} sm={12} md={8} lg={8}>
                <DatePicker.RangePicker
                  name={'date_range'}
                  presets={rangePresets}
                  format={'YYYY-MM-DD'}
                  onChange={(e: any) => setUrlParamsFromObject(formatDateRangeArray(e))}
                />
              </Col>
              <FormInput
                name={'search'}
                placeholder='Search here ...'
                onChange={(e: string) => handelSearch(e.trim())}
              />
            </Row>
          </Form>
        </Col>
      </Row>
      <div>
        <span style={{ marginLeft: '5px', float: 'left', marginBottom: '1rem' }}>
          You have total <strong>{data?.count || data?.data?.length || 0}</strong> rows.
        </span>
        <Table
          columns={QuotationUtils(pagination, permission)}
          bordered
          dataSource={dataWithKey}
          scroll={{ x: 'max-content' }}
          size='small'
          pagination={changePagination(data?.count)}
          loading={{
            spinning: dataIsLoading || isFetching,
            indicator: loadingIndicator,
          }}
        />
      </div>
    </div>
  );
};

export default QuotationList;
